import * as React from 'react';

import type { ChatProfile, MessageDto } from '@/api/chat-client';
import {
  useMarkRead,
  useSendMessage,
  useThreadMessagesQuery,
} from '@/api/use-chat';
import { useChatRealtime } from '@/api/use-chat-realtime';
import type { ChatSocketLike } from '@/api/chat-realtime';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  formatDateTime,
  senderRoleLabel,
  threadStateLabel,
} from '@/routes/communication/chat-format';
import { cn } from '@/lib/utils';

// =============================================================================
// chat-thread-panel.tsx — Panneau droit : fil chronologique + composition (R15-COM-02).
// =============================================================================
// Le MONTANT est 100% REST (DEC-31 §1) : messages via useThreadMessagesQuery ; envoi via useSendMessage ;
// marquage-lu (à l'ouverture ET au focus fenêtre) via useMarkRead. Le socket (useChatRealtime) ne fait
// QUE déclencher des refetch — le CONTENU affiché vient TOUJOURS du REST RLS.
//
// SÉCURITÉ DE RENDU (DEC-31) : le `content` est du TEXTE BRUT rendu JSX ÉCHAPPÉ (`{message.content}`),
// JAMAIS `dangerouslySetInnerHTML` — un contenu type `<img onerror=...>` s'affiche comme TEXTE, jamais
// interprété. WCAG 2.1 AA : région titrée, liste de messages, indicateur « lu » en LIBELLÉ TEXTE (jamais
// une couleur seule), composition avec <label> associé, envoi au bouton (Entrée = nouvelle ligne).

export interface ChatThreadPanelProps {
  readonly profile: ChatProfile;
  readonly threadId: string;
  /** Fabrique de socket injectable (tests) — transmise au hook temps réel. */
  readonly socketFactory?: () => ChatSocketLike;
  /**
   * Masque l'en-tête interne du panneau. Optionnel (défaut `false` → comportement inchangé pour les
   * usages existants : chat-workspace admin). Permet à un écran hôte (ex. « Mes échanges avec JeKo »)
   * de porter SON PROPRE en-tête unifié au-dessus du panneau, sans double bandeau.
   */
  readonly hideHeader?: boolean;
  /** Placeholder du champ de composition (optionnel ; défaut « Écrivez votre message… »). */
  readonly composerPlaceholder?: string;
  /** Texte de l'état « aucun message » (optionnel ; défaut parité base). */
  readonly emptyStateText?: string;
}

export function ChatThreadPanel({
  profile,
  threadId,
  socketFactory,
  hideHeader = false,
  composerPlaceholder = 'Écrivez votre message…',
  emptyStateText = 'Aucun message pour le moment. Écrivez le premier message ci-dessous.',
}: ChatThreadPanelProps): React.JSX.Element {
  const messagesQuery = useThreadMessagesQuery(profile, threadId);
  const sendMutation = useSendMessage(profile, threadId);
  const markReadMutation = useMarkRead(profile, threadId);
  const [draft, setDraft] = React.useState('');

  // Socket temps réel : JOIN du fil + invalidations sur signal mince (aucun contenu lu du payload).
  useChatRealtime(
    threadId,
    socketFactory !== undefined ? { socketFactory } : {},
  );

  // Marquage-lu à l'OUVERTURE du fil (threadId change) — l'appelant a « vu » le fil.
  const markRead = markReadMutation.mutate;
  React.useEffect(() => {
    markRead();
  }, [threadId, markRead]);

  // Marquage-lu au retour de FOCUS de la fenêtre (l'utilisateur revient sur le fil ouvert).
  React.useEffect(() => {
    const onFocus = (): void => {
      markRead();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [markRead]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const content = draft.trim();
    if (content.length === 0 || sendMutation.isPending) {
      return;
    }
    sendMutation.mutate(content, {
      onSuccess: () => {
        // Le contenu affiché vient du refetch REST (invalidation dans le hook) ; on vide juste la saisie.
        setDraft('');
      },
    });
  };

  if (messagesQuery.isLoading) {
    return (
      <p role="status" className="p-6 text-sm text-muted-foreground">
        Chargement de la conversation…
      </p>
    );
  }

  if (messagesQuery.isError || messagesQuery.data === undefined) {
    return (
      <p role="alert" className="p-6 text-sm text-destructive">
        {messagesQuery.error?.message ??
          'La conversation est momentanément indisponible.'}
      </p>
    );
  }

  const thread = messagesQuery.data;
  const composerId = `chat-composer-${threadId}`;

  return (
    <section
      aria-label="Conversation"
      className="flex h-full min-h-0 flex-col"
    >
      {hideHeader ? null : (
        <header className="border-b border-border px-6 py-4">
          {/* P-6 : « Fournisseur 53993947000016 » ne disait à personne avec qui l'on parle. On nomme
              l'entreprise, et on ne retombe sur le SIRET que si le référentiel l'ignore. */}
          <h2
            className="font-serif text-base font-semibold"
            title={profile === 'admin' ? thread.supplierSiret : undefined}
          >
            {profile === 'admin'
              ? (thread.supplierRaisonSociale ?? `Fournisseur ${thread.supplierSiret}`)
              : 'Conversation avec JeKo'}
          </h2>
          <p className="text-xs text-muted-foreground">
            Campagne {thread.campaignId} · {threadStateLabel(thread.state)}
          </p>
        </header>
      )}

      <div className="flex-1 overflow-y-auto px-6 py-4">
        {thread.messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyStateText}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {thread.messages.map((message) => (
              <ChatMessageBubble key={message.messageId} message={message} />
            ))}
          </ul>
        )}
      </div>

      <form
        onSubmit={onSubmit}
        className="border-t border-border px-6 py-4"
      >
        <Label htmlFor={composerId}>Votre message</Label>
        <Textarea
          id={composerId}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={composerPlaceholder}
          className="mt-1.5 min-h-24"
        />
        <div className="mt-2 flex items-center justify-end gap-3">
          {sendMutation.isError ? (
            <p role="alert" className="text-xs text-destructive">
              {sendMutation.error?.message ?? "L'envoi a échoué."}
            </p>
          ) : null}
          <Button
            type="submit"
            disabled={draft.trim().length === 0 || sendMutation.isPending}
          >
            {sendMutation.isPending ? 'Envoi…' : 'Envoyer'}
          </Button>
        </div>
      </form>
    </section>
  );
}

/** Une bulle de message. `mine` aligne à droite ; l'indicateur « lu » n'apparaît que sur mes messages. */
function ChatMessageBubble({
  message,
}: {
  readonly message: MessageDto;
}): React.JSX.Element {
  return (
    <li
      className={cn(
        'flex flex-col',
        message.mine ? 'items-end' : 'items-start',
      )}
    >
      <div
        className={cn(
          'max-w-[80%] rounded-lg border px-3 py-2 text-sm',
          message.mine
            ? 'border-primary/30 bg-primary/10 text-foreground'
            : 'border-border bg-card text-card-foreground',
        )}
      >
        {/* TEXTE BRUT rendu ÉCHAPPÉ (DEC-31) : jamais dangerouslySetInnerHTML. */}
        <p className="whitespace-pre-wrap break-words">{message.content}</p>
      </div>
      <p className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
        <span>{senderRoleLabel(message.senderRoleAtSend)}</span>
        <span aria-hidden="true">·</span>
        <span>{formatDateTime(message.sentAt)}</span>
        {message.mine ? (
          <>
            <span aria-hidden="true">·</span>
            <span>
              {message.readByCounterpart
                ? `Lu${
                    message.firstReadAt !== null
                      ? ` le ${formatDateTime(message.firstReadAt)}`
                      : ''
                  }`
                : 'Envoyé'}
            </span>
          </>
        ) : null}
      </p>
    </li>
  );
}
